import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { UserPlus, Loader2, Eye, EyeOff, ArrowLeft, Check } from 'lucide-react';
import { toast } from 'sonner';

const RegisterPage = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/register', { name, email, password });
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail.map(e => e.msg || '').join(' ')
        : 'Erreur';
      setError(msg);
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[rgb(var(--lm-bg))] flex items-center justify-center p-4" data-testid="register-success">
        <div className="w-full max-w-sm text-center">
          <div className="w-14 h-14 rounded-full bg-[rgb(var(--lm-green)/0.1)] flex items-center justify-center mx-auto mb-4">
            <Check size={24} className="text-[rgb(var(--lm-green))]" />
          </div>
          <h1 className="font-title text-lg text-[rgb(var(--lm-text))] mb-2">Inscription reussie !</h1>
          <p className="text-sm text-[rgb(var(--lm-text-muted))] mb-6">
            Votre compte est en attente de validation par le patron du restaurant. Vous recevrez un acces des que votre compte sera approuve.
          </p>
          <Link to="/login">
            <Button className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] hover:opacity-90">
              Retour a la connexion
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[rgb(var(--lm-bg))] flex items-center justify-center p-4" data-testid="register-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-1 text-[10px] text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-gold))] mb-6">
            <ArrowLeft size={12} /> Retour
          </Link>
          <h1 className="font-title text-xl tracking-[0.2em] text-[rgb(var(--lm-gold))]">LE MAS</h1>
          <p className="text-xs text-[rgb(var(--lm-text-muted))] mt-1">Creer votre compte</p>
        </div>

        <form onSubmit={handleRegister} className="lm-card p-6 space-y-4">
          {error && (
            <div className="p-3 rounded-sm bg-[rgb(var(--lm-red)/0.08)] text-xs text-[rgb(var(--lm-red))]" data-testid="register-error">
              {error}
            </div>
          )}

          <div>
            <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Nom complet</label>
            <Input value={name} onChange={e => setName(e.target.value)}
              className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]"
              placeholder="Votre nom" autoFocus data-testid="register-name" />
          </div>

          <div>
            <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Email</label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)}
              className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]"
              placeholder="votre@email.com" data-testid="register-email" />
          </div>

          <div>
            <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Mot de passe</label>
            <div className="relative">
              <Input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] pr-9"
                placeholder="Minimum 6 caracteres" data-testid="register-password" />
              <button type="button" onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[rgb(var(--lm-text-muted))]">
                {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>

          <Button type="submit" disabled={loading || !name || !email || !password}
            className="w-full bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] hover:opacity-90 py-5 gap-2"
            data-testid="register-submit">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
            S'inscrire
          </Button>

          <p className="text-center text-xs text-[rgb(var(--lm-text-muted))]">
            Deja un compte ?{' '}
            <Link to="/login" className="text-[rgb(var(--lm-gold))] hover:underline" data-testid="goto-login">
              Se connecter
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default RegisterPage;
